/**
 * Updates meta tags dynamically based on the center configuration
 * This ensures text message previews show the correct center information
 */
export const updateMetaTags = (config) => {
  if (!config || !config.name) return;

  // Helper function to update or create a meta tag
  const setMetaTag = (property, content, isOpenGraph = false) => {
    const attributeName = isOpenGraph ? 'property' : 'name';
    let tag = document.querySelector(`meta[${attributeName}="${property}"]`);
    
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attributeName, property);
      document.head.appendChild(tag);
    }
    
    tag.setAttribute('content', content);
  };

  // Prepare the title and description
  const title = config.name;
  const description = config.about_us_content?.intro || 
    `Access lecture summaries, videos, and notes from ${config.name}`;

  // Use the logo URL from config with proper fallback chain for text messaging apps
  const getPentaFallbackUrl = () => `${window.location.origin}/images/penta-lectures-logo.png`;
  
  let imageUrl = config.logo_url;
  
  // If no logo_url in config, use Penta Lectures logo as fallback
  if (!imageUrl) {
    console.log('[Meta Tags] No logo_url in config, using Penta Lectures fallback');
    imageUrl = getPentaFallbackUrl();
  } else {
    console.log('[Meta Tags] Using center logo:', imageUrl);
  }
  
  // Remove cache-busting as per expert recommendation - it breaks social media analytics
  // and causes platforms to treat each URL as a new resource

  // Update basic meta tags
  setMetaTag('description', description);
  
  // Ensure proper URL formatting for og:url
  let siteUrl = config.domain;
  if (siteUrl && !siteUrl.startsWith('http')) {
    siteUrl = `https://${siteUrl}`;
  }
  if (!siteUrl) {
    siteUrl = window.location.origin;
  }

  // Update Open Graph tags
  setMetaTag('og:title', title, true);
  setMetaTag('og:description', description, true);
  setMetaTag('og:image', imageUrl, true);
  setMetaTag('og:url', siteUrl, true);
  setMetaTag('og:type', 'website', true);
  setMetaTag('og:site_name', `${config.name} - Lecture Platform`, true);
  
  // Update Twitter Card tags
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', title);
  setMetaTag('twitter:description', description);
  setMetaTag('twitter:image', imageUrl);

  // Update the page title
  document.title = title;

  // Update theme color if available
  if (config.colors?.primary) {
    setMetaTag('theme-color', config.colors.primary);
  }

  // Update Apple-specific PWA meta tags
  const shortName = config.short_name || config.name || 'Lecture Platform';
  const appleName = `${shortName} - Lecture Platform`;
  
  setMetaTag('apple-mobile-web-app-title', appleName);
  setMetaTag('apple-mobile-web-app-capable', 'yes');
  setMetaTag('apple-mobile-web-app-status-bar-style', 'default');
  setMetaTag('mobile-web-app-capable', 'yes');
  
  // Update apple touch icon if logo_url is available
  if (config.logo_url) {
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouchIcon);
    }
    // For now, use the logo_url directly (icon generator will create proper sized icons)
    appleTouchIcon.href = config.logo_url;
  }
};

/**
 * Resets meta tags to default values
 */
export const resetMetaTags = () => {
  const defaultTitle = 'Community Hub';
  const defaultDescription = 'Access lecture summaries, videos, and notes';
  
  document.title = defaultTitle;
  
  // Update to default values
  const setMetaTag = (property, content, isOpenGraph = false) => {
    const attributeName = isOpenGraph ? 'property' : 'name';
    const tag = document.querySelector(`meta[${attributeName}="${property}"]`);
    if (tag) {
      tag.setAttribute('content', content);
    }
  };
  
  setMetaTag('description', defaultDescription);
  setMetaTag('og:title', defaultTitle, true);
  setMetaTag('og:description', defaultDescription, true);
  setMetaTag('twitter:title', defaultTitle);
  setMetaTag('twitter:description', defaultDescription);
};